/**
 * The shell's reading of the extraction ledger.
 *
 * The ledger on the source grid and the Documents page stays the record of what
 * happened; this is the one-line answer to "is anything happening, and does it
 * want me?" that the app shell can carry from any page.
 *
 * Runs are grouped, never merged: three concurrent extractions are three rows
 * with three document tallies, and the summary only ever counts them.
 */
import type { ExtractionRun } from '@/lib/extraction-runs/extraction-run-store';
import { isTerminalExtractionRunPhase } from '@/lib/extraction-runs/extraction-run-store';

/**
 * `attention` outranks `active` because an unresolved outcome is the only state
 * that asks the reviewer for something; `ready` is the quietest, a success that
 * has not been dismissed yet.
 */
export type ExtractionActivityTone = 'attention' | 'active' | 'ready';

export interface ExtractionActivitySummary {
  /** Attention first, then in-flight, then undismissed successes. */
  readonly runs: readonly ExtractionRun[];
  readonly attention: readonly ExtractionRun[];
  readonly active: readonly ExtractionRun[];
  readonly ready: readonly ExtractionRun[];
  /** `null` when the project has nothing to say. */
  readonly tone: ExtractionActivityTone | null;
  /** How many runs sit in the dominant tone. */
  readonly count: number;
}

function extractionRunTone(run: ExtractionRun): ExtractionActivityTone {
  if (!isTerminalExtractionRunPhase(run.phase)) {
    return 'active';
  }
  return run.phase === 'succeeded' ? 'ready' : 'attention';
}

export function summariseExtractionActivity(
  runs: readonly ExtractionRun[],
): ExtractionActivitySummary {
  const attention = runs.filter((run) => extractionRunTone(run) === 'attention');
  const active = runs.filter((run) => extractionRunTone(run) === 'active');
  const ready = runs.filter((run) => extractionRunTone(run) === 'ready');
  const groups: readonly (readonly [ExtractionActivityTone, readonly ExtractionRun[]])[] = [
    ['attention', attention],
    ['active', active],
    ['ready', ready],
  ];
  const dominant = groups.find(([, group]) => group.length > 0);
  const tone = dominant?.[0] ?? null;
  const count = dominant?.[1].length ?? 0;
  return {
    runs: [...attention, ...active, ...ready],
    attention,
    active,
    ready,
    tone,
    count,
  };
}

/** The word on the chip. The full sentence lives in the accessible name. */
export function extractionActivityToneLabel(tone: ExtractionActivityTone): string {
  switch (tone) {
    case 'attention':
      return 'Needs review';
    case 'active':
      return 'Extracting';
    case 'ready':
      return 'Ready';
  }
}

function countPhrase(count: number, singular: string, plural: string): string {
  return `${String(count)} ${count === 1 ? singular : plural}`;
}

/**
 * One sentence that carries every state the chip is showing, so the affordance
 * reads the same to a screen reader as it looks — and so neither colour nor
 * motion is ever the only signal.
 */
export function describeExtractionActivity(summary: ExtractionActivitySummary): string {
  const parts: string[] = [];
  if (summary.attention.length > 0) {
    parts.push(
      `${countPhrase(summary.attention.length, 'extraction', 'extractions')} ${
        summary.attention.length === 1 ? 'needs' : 'need'
      } review`,
    );
  }
  if (summary.active.length > 0) {
    parts.push(`${countPhrase(summary.active.length, 'extraction', 'extractions')} running`);
  }
  if (summary.ready.length > 0) {
    parts.push(
      `${countPhrase(summary.ready.length, 'finished extraction', 'finished extractions')} to open`,
    );
  }
  return parts.length === 0 ? 'No extraction activity' : parts.join(', ');
}
